import React, { useState, useMemo } from 'react';
import { Card, Space, Typography, Skeleton, Empty, Button, Tooltip, Progress, Modal, List, Row, Col, Segmented, Statistic, Tag } from 'antd';
import { CopyOutlined, DownloadOutlined, ChevronRightOutlined, TeamOutlined, CalendarOutlined, CheckCircleOutlined, TrophyOutlined, ClockCircleOutlined, WarningOutlined, FileExcelOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { Method_POST_REST } from '../../../../../service/api';
import { toast } from 'react-toastify';

const { Text, Title } = Typography;

const OBRIGACAO_METADATA = {
  1: { day: "30", month: "12", label: "Anual (Dez)", period: 'Anual' },
  9: { day: "31", month: "03", label: "Anual (Mar)", period: 'Anual' },
  14: { day: "99", month: "05", label: "Anual (Maio)", period: 'Anual' },
  15: { day: "99", month: "07", label: "Anual (Jul)", period: 'Anual' },
  41: { day: "30", month: "12", label: "Anual (Dez)", period: 'Anual' },
  255: { day: "99", month: "01;07", label: "Semestral", period: 'Trimestral' },
  308: { day: "99", month: "12", label: "Anual", period: 'Anual' },
  2586: { day: "99", month: "04", label: "1º Tri (Abr)", period: 'Trimestral' },
  2587: { day: "99", month: "10", label: "3º Tri (Out)", period: 'Trimestral' },
};

const YearlyObligations = ({ loading, data, onExport, dataPosicao }) => {
  const [selectedOblig, setSelectedOblig] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [filter, setFilter] = useState('Todos');

  const processedData = useMemo(() => {
    if (!data) return [];
    const base = data.map(item => ({
      ...item,
      metadata: OBRIGACAO_METADATA[item.obrigacao.id] || { day: "-", month: "-", label: "N/A", period: 'Outros' }
    }));

    const filtered = filter === 'Todos' ? base : base.filter(item => item.metadata.period === filter);

    return filtered.sort((a, b) => {
      const m1 = isNaN(a.metadata.month) ? 99 : parseInt(a.metadata.month);
      const m2 = isNaN(b.metadata.month) ? 99 : parseInt(b.metadata.month);
      return m1 - m2;
    });
  }, [data, filter]);

  const stats = useMemo(() => {
    if (!processedData.length) return { total: 0, emitido: 0, previsto: 0, percent: 0 };
    const totalEmitido = processedData.reduce((acc, curr) => acc + curr.quantidadeEmitido, 0);
    const totalPrevisto = processedData.reduce((acc, curr) => acc + curr.quantidadePrevisto, 0);
    return {
      total: processedData.length,
      emitido: totalEmitido,
      previsto: totalPrevisto,
      percent: totalPrevisto > 0 ? Math.round((totalEmitido / totalPrevisto) * 100) : 0
    };
  }, [processedData]);

  const fetchDetails = async (obrig) => {
    setSelectedOblig(obrig);
    setDetailsLoading(true);
    try {
      const res = await Method_POST_REST("dashboard/indicador/calcular", {
        id: String(obrig.id),
        dataPosicao: dayjs(dataPosicao).format('YYYY-MM-DD')
      });
      setDetails(res);
    } catch (e) {
      toast.error("Erro ao carregar detalhes da obrigação.");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleExportDetails = () => {
    if (!details?.indicadores) return;
    try {
      const exportData = details.indicadores.map(item => ({
        'Unidade de Negócio (Célula)': item.nome,
        'Previsto': item.metaMensal?.previsto || 0,
        'Realizado': item.metaMensal?.realizado || 0,
        'Taxa de Entrega (%)': item.metaMensal?.taxa || 0,
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Desempenho Células");
      XLSX.writeFile(wb, `Desempenho_${selectedOblig.sigla}_${dayjs().format('DD-MM-YYYY')}.xlsx`);
      toast.success("Excel de detalhes exportado!");
    } catch (e) {
      toast.error("Erro ao exportar detalhes.");
    }
  };

  return (
    <>
      <Card
        title={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ background: '#f8fafc', padding: 8, borderRadius: 12, display: 'flex' }}>
                <CopyOutlined style={{ color: '#2563eb', fontSize: 20 }} />
              </div>
              <div>
                <Title level={5} style={{ margin: 0, fontWeight: 800 }}>Obrigações Contábeis</Title>
                <Text type="secondary" style={{ fontSize: 12 }}>Entrega anual e trimestral</Text>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Segmented
                size="small"
                options={['Todos', 'Trimestral', 'Anual']}
                value={filter}
                onChange={setFilter}
                style={{ background: '#f1f5f9', borderRadius: 10, padding: 2 }}
              />
              {onExport && (
                <Button type="text" icon={<DownloadOutlined style={{ fontSize: 18 }} />} onClick={onExport} style={{ color: '#94a3b8' }} />
              )}
            </div>
          </div>
        }
        bordered={false}
        style={{ borderRadius: 24, boxShadow: '0 10px 40px rgba(0,0,0,0.04)', height: '100%' }}
      >
        {loading ? (
          <Skeleton active paragraph={{ rows: 12 }} />
        ) : !data || data.length === 0 ? (
          <Empty description="Sem dados" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
            {/* Resumo Centralizado */}
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              <Text type="secondary" style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                Total de Obrigações
              </Text>
              <div style={{ fontSize: 42, fontWeight: 900, color: '#0f172a', lineHeight: 1.2 }}>
                {stats.previsto.toLocaleString('pt-BR')}
              </div>
            </div>

            {/* Lista de Itens Estilo Open Finance */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxHeight: 450, overflowY: 'auto', paddingRight: 8 }}>
              {processedData.map((item) => {
                const percent = item.quantidadePrevisto > 0 ? Math.round((item.quantidadeEmitido / item.quantidadePrevisto) * 100) : 0;
                let statusColor = '#3b82f6'; // Azul padrão
                if (percent === 100) statusColor = '#10b981'; // Verde concluído
                if (percent < 50 && percent > 0) statusColor = '#f59e0b'; // Laranja alerta
                if (percent === 0) statusColor = '#ef4444'; // Vermelho crítico

                return (
                  <div 
                    key={item.obrigacao.id} 
                    onClick={() => fetchDetails(item.obrigacao)}
                    style={{ cursor: 'pointer' }}
                    className="oblig-item-row"
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <Space>
                        <div style={{ color: statusColor, display: 'flex' }}>
                          {percent === 100 ? <CheckCircleOutlined style={{ fontSize: 18 }} /> : 
                           percent === 0 ? <WarningOutlined style={{ fontSize: 18 }} /> : 
                           <ClockCircleOutlined style={{ fontSize: 18 }} />}
                        </div>
                        <Text strong style={{ fontSize: 14, color: '#1e293b' }}>{item.obrigacao.sigla}</Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>({item.metadata.label})</Text>
                      </Space>
                      <Space>
                        <Text style={{ fontSize: 13, color: '#64748b' }}>
                          <span style={{ color: statusColor, fontWeight: 700 }}>{item.quantidadeEmitido}</span>
                          <span style={{ margin: '0 4px', opacity: 0.5 }}>/</span>
                          {item.quantidadePrevisto} Clientes
                        </Text>
                        <Text strong style={{ fontSize: 14, color: '#0f172a', minWidth: 45, textAlign: 'right' }}>{percent}%</Text>
                      </Space>
                    </div>
                    <Progress 
                      percent={percent} 
                      showInfo={false} 
                      strokeColor={statusColor}
                      strokeWidth={8}
                      trailColor="#f1f5f9"
                      style={{ margin: 0 }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '95%' }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>Células: {selectedOblig?.sigla}</Title>
              <Text type="secondary" style={{ fontSize: 12 }}>Desempenho por unidade de negócio</Text>
            </div>
            {details && (
              <Button 
                type="primary" 
                ghost 
                size="small" 
                icon={<FileExcelOutlined style={{ fontSize: 16 }} />} 
                onClick={handleExportDetails}
                style={{ borderRadius: 8 }}
              >
                Exportar Células
              </Button>
            )}
          </div>
        }
        open={!!selectedOblig}
        onCancel={() => setSelectedOblig(null)}
        footer={null}
        width={550}
        centered
        styles={{ 
          mask: { backdropFilter: 'blur(4px)', background: 'rgba(15, 23, 42, 0.4)' },
          body: { padding: '8px 0 24px 0' } 
        }}
      >
        {detailsLoading ? (
          <div style={{ padding: 24 }}><Skeleton active /></div>
        ) : (
          <List
            dataSource={details?.indicadores || []}
            renderItem={(item) => {
              const itemPercent = item.metaMensal?.taxa || 0;
              let itemColor = '#3b82f6';
              if (itemPercent >= 100) itemColor = '#10b981';
              else if (itemPercent < 50 && itemPercent > 0) itemColor = '#f59e0b';
              else if (itemPercent === 0) itemColor = '#ef4444';

              return (
                <List.Item style={{ padding: '12px 24px', border: 'none' }}>
                  <div style={{ width: '100%', background: '#f8fafc', padding: 16, borderRadius: 20, border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <Space>
                        <div style={{ background: '#fff', padding: 6, borderRadius: 8, display: 'flex', color: itemColor }}>
                          <TeamOutlined style={{ fontSize: 16 }} />
                        </div>
                        <Text strong style={{ fontSize: 14, color: '#334155' }}>{item.nome}</Text>
                      </Space>
                      <Space>
                        <Text style={{ fontSize: 13, color: '#64748b' }}>
                          <span style={{ color: itemColor, fontWeight: 700 }}>{item.metaMensal?.realizado || 0}</span>
                          <span style={{ margin: '0 4px', opacity: 0.5 }}>/</span>
                          {item.metaMensal?.previsto || 0}
                        </Text>
                        <Tag color={itemPercent >= 100 ? 'success' : 'processing'} style={{ borderRadius: 6, margin: 0, border: 'none' }}>
                          {itemPercent}%
                        </Tag>
                      </Space>
                    </div>
                    <Progress 
                      percent={itemPercent} 
                      strokeColor={itemColor}
                      size="small" 
                      trailColor="#e2e8f0"
                      strokeWidth={6}
                    />
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </Modal>

      <style dangerouslySetInnerHTML={{ __html: `
        .oblig-item-row:hover { opacity: 0.8; }
        .oblig-item-row { transition: all 0.2s ease; }
      `}} />
    </>
  );
};

export default YearlyObligations;
